import axios from 'axios';
import { iframeDataBridge } from '../iframe/iframeDataBridge.js';
import { getApiUrl, getAuthHeaders } from '@/utils/auth/authService.js';
import { log } from '@/utils/core/vueLogger.js';

let travelMode = 'car';

function getEcosOptions() {
	const ecosVM = iframeDataBridge.getEcosVM(true);
	return ecosVM?.options || {};
}

function isGoogleAllowed() {
	const options = getEcosOptions();
	const googleOption = options.find((o) => o.name == 'GeoProviderGoogle' && o.application === 'SALFO');
	const preferGoogle = String(googleOption?.value).trim().toLowerCase() === 'true';
	const provider = preferGoogle ? 'google' : 'osrm';

	if (provider !== 'google') return false;

	const maxRemoteCall = Number(options.find((o) => o.name == 'MaxRemoteCall.google.maps.DirectionsService')?.value) || 0;
	const remoteCallCount = Number(options.find((o) => o.name == 'MaxRemoteCall.google.maps.DirectionsService')?.currentValue) || 0;
	if (maxRemoteCall <= 0) return true;
	return provider === 'google' && remoteCallCount < maxRemoteCall;
}

function incGoogleCall() {
	const options = getEcosOptions();
	const option = options.find((o) => o.name == 'MaxRemoteCall.google.maps.DirectionsService');
	if (option) {
		option.currentValue = ((Number(option.currentValue) || 0) + 1).toString();
	}

	const url = getApiUrl(`/Core/Options`);
	const headersPromise = getAuthHeaders({}, 'token');
	headersPromise.then((headers) => {
		axios.post(url, [option], { headers: headers });
	});
}

function formatDurationSeconds(durationSeconds) {
	if (typeof durationSeconds !== 'number' || Number.isNaN(durationSeconds)) return '';
	const totalMinutes = Math.round(durationSeconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	const hh = String(hours).padStart(2, '0');
	const mm = String(minutes).padStart(2, '0');
	return `${hh}h${mm}`;
}

function formatDistanceMeters(distanceMeters) {
	if (typeof distanceMeters !== 'number' || Number.isNaN(distanceMeters)) return '';
	const km = distanceMeters / 1000;
	const rounded = km >= 10 ? Math.round(km) : Math.round(km * 10) / 10;
	return `${rounded} km`;
}

function buildGeoJsonLine(coordinates, distanceText, durationText, distanceMeters, durationSeconds) {
	return {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				geometry: {
					type: 'LineString',
					coordinates: coordinates,
				},
				properties: {
					distance: distanceText,
					duration: durationText,
					distance_value: distanceMeters,
					duration_value: durationSeconds,
				},
			},
		],
	};
}

const OSRM_CHUNK_SIZE = 10;

// The eCOS maps proxy rejects any URL path segment longer than 260 characters (no CORS headers, so the call just
// fails), which caps the coordinate list of one OSRM call. 6 decimals is OSRM's own precision: nothing is lost.
const MAX_COORDINATES_LENGTH = 260;
const toCoordinateText = ([lng, lat]) => `${Number(Number(lng).toFixed(6))},${Number(Number(lat).toFixed(6))}`;

async function fetchOSRMChunk(waypoints, headers) {
	const coords = waypoints.map(toCoordinateText).join(';');
	const url = getMapsApiUrl(`/osrm/route/v1/driving/${coords}`);
	const response = await axios.get(url, {
		params: { overview: 'full', geometries: 'geojson', steps: 'false' },
		headers,
	});
	const route = response?.data?.routes?.[0] || null;
	const legs = route?.legs || null;
	if (!route || !route.geometry || !route.geometry.coordinates || !legs || !legs.length) {
		throw new Error('OSRM route format invalid');
	}
	let distanceMeters = 0;
	let durationSeconds = 0;
	legs.forEach((leg) => {
		distanceMeters += Number(leg.distance || 0);
		durationSeconds += Number(leg.duration || 0);
	});
	return { coordinates: route.geometry.coordinates, distanceMeters, durationSeconds };
}

async function getRouteWithOSRM(waypoints) {
	if (!Array.isArray(waypoints) || waypoints.length < 2) {
		throw new Error('OSRM requires at least 2 waypoints');
	}

	const headers = await getAuthHeaders({}, 'token');

	// Split into overlapping chunks of OSRM_CHUNK_SIZE (last point of chunk N = first of chunk N+1)
	const chunks = [];
	for (let i = 0; i < waypoints.length - 1; i += OSRM_CHUNK_SIZE - 1) {
		chunks.push(waypoints.slice(i, i + OSRM_CHUNK_SIZE));
	}

	const results = await Promise.all(chunks.map((chunk) => fetchOSRMChunk(chunk, headers)));

	// Merge: concatenate coordinates skipping the duplicate junction point of each subsequent chunk
	let allCoordinates = results[0].coordinates;
	let totalDistance = results[0].distanceMeters;
	let totalDuration = results[0].durationSeconds;

	for (let i = 1; i < results.length; i++) {
		allCoordinates = allCoordinates.concat(results[i].coordinates.slice(1));
		totalDistance += results[i].distanceMeters;
		totalDuration += results[i].durationSeconds;
	}

	return buildGeoJsonLine(allCoordinates, formatDistanceMeters(totalDistance), formatDurationSeconds(totalDuration), totalDistance, totalDuration);
}

function getMapsApiUrl(path) {
	let mapUrl;
	switch (window.location.hostname) {
		case 'm.ecos2.externis.com':
			mapUrl = 'https://maps.ecos2.externis.com';
			break;
		case 'm.ecos.externis.net':
			mapUrl = 'https://maps.ecos2.externis.net';
			break;
		default:
			mapUrl = 'https://maps.ecos.externis.net';
	}
	return `${mapUrl}${path}`;
}

async function getRouteWithGoogleMaps(waypoints) {
	if (!Array.isArray(waypoints) || waypoints.length < 2) {
		throw new Error('Google Maps requires at least 2 waypoints');
	}

	const iframeWin = iframeDataBridge.getIframeWindow();
	const google = iframeWin?.google;

	if (!google || !google.maps) {
		throw new Error('Google Maps API not available');
	}

	if (!isGoogleAllowed()) {
		throw new Error('Google Maps call limit reached');
	}

	incGoogleCall();

	const originPoint = waypoints[0];
	const destinationPoint = waypoints[waypoints.length - 1];
	const intermediateWaypoints = waypoints.slice(1, waypoints.length - 1);

	const travelModeMap = { walking: 'WALK', bike: 'BICYCLE', car: 'DRIVE' };
	const makeWaypoint = (p) => ({ location: { latLng: { latitude: p[1], longitude: p[0] } } });

	const request = {
		origin: makeWaypoint(originPoint),
		destination: makeWaypoint(destinationPoint),
		intermediates: intermediateWaypoints.map(makeWaypoint),
		travelMode: travelModeMap[travelMode] || 'DRIVE',
		computeAlternativeRoutes: false,
		polylineQuality: 'HIGH_QUALITY',
		polylineEncoding: 'ENCODED_POLYLINE',
	};

	let RouteClass = google.maps.routes?.Route;
	if (!RouteClass && typeof google.maps.importLibrary === 'function') {
		const routesLib = await google.maps.importLibrary('routes');
		RouteClass = routesLib.Route || routesLib.RoutesClient;
	}
	if (!RouteClass) {
		throw new Error('google.maps.routes.Route not available');
	}

	const routesClient = new RouteClass();
	const response = await routesClient.computeRoutes(request, {
		otherArgs: {
			headers: {
				'X-Goog-FieldMask': 'routes.legs.distanceMeters,routes.legs.duration,routes.polyline.encodedPolyline',
			},
		},
	});

	const route = response?.routes?.[0] || null;
	const legs = route?.legs || null;
	const encodedPolyline = route?.polyline?.encodedPolyline || null;

	if (!route || !legs || !legs.length || !encodedPolyline) {
		throw new Error('Google route format invalid');
	}

	let distanceMeters = 0;
	let durationSeconds = 0;

	legs.forEach((leg) => {
		distanceMeters += Number(leg?.distanceMeters || 0);
		durationSeconds += Math.round(parseFloat(leg?.duration) || 0);
	});

	const path = google.maps.geometry.encoding.decodePath(encodedPolyline);
	const coordinates = path.map((p) => [p.lng(), p.lat()]);
	const distanceText = formatDistanceMeters(distanceMeters);
	const durationText = formatDurationSeconds(durationSeconds);

	return buildGeoJsonLine(coordinates, distanceText, durationText, distanceMeters, durationSeconds);
}

async function getRoute(waypoints, forcedProvider = null) {
	if (isGoogleAllowed()) {
		return await getRouteWithGoogleMaps(waypoints);
	}
	return await getRouteWithOSRM(waypoints);
}

function setTravelMode(mode) {
	if (['car', 'walking', 'bike'].includes(mode)) {
		travelMode = mode;
	}
}

function getTravelMode() {
	return travelMode;
}

async function getRouteWithFallback(waypoints) {
	if (isGoogleAllowed()) {
		try {
			return await getRouteWithGoogleMaps(waypoints);
		} catch (e) {
			return await getRouteWithOSRM(waypoints);
		}
	}

	return await getRouteWithOSRM(waypoints);
}

// Straight-line estimate used only for pairs OSRM can't route (null cell): ~50 km/h with a 1.3 road detour.
function estimateMinutes([lngA, latA], [lngB, latB]) {
	const rad = Math.PI / 180;
	const dLat = (latB - latA) * rad;
	const dLng = (lngB - lngA) * rad;
	const a = Math.sin(dLat / 2) ** 2 + Math.cos(latA * rad) * Math.cos(latB * rad) * Math.sin(dLng / 2) ** 2;
	const km = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return Math.ceil(((km * 1.3) / 50) * 60);
}

// A matrix call never waits longer than this: a hanging proxy fails the optimization instead of spinning forever.
const MATRIX_TIMEOUT_MS = 20000;

// Driving minutes of one pair, without the geometry.
async function fetchPairMinutes(from, to, headers) {
	const response = await axios.get(getMapsApiUrl(`/osrm/route/v1/driving/${toCoordinateText(from)};${toCoordinateText(to)}`), { params: { overview: 'false', steps: 'false' }, headers, timeout: MATRIX_TIMEOUT_MS });
	const seconds = response?.data?.routes?.[0]?.duration;
	if (response?.data?.code !== 'Ok' || !Number.isFinite(seconds)) throw new Error('OSRM route format invalid');
	return Math.ceil(seconds / 60);
}

/**
 * Fallback of a failed table call: one route call per pair, a pair that fails is estimated. When the first
 * calls all fail, OSRM is unreachable: the error is raised rather than returning a matrix of estimates.
 *
 * @returns {Promise<{minutes: number[][], complete: boolean}>}  complete = no estimated pair
 */
async function getPairwiseDurations(coordinates, headers) {
	const minutes = coordinates.map(() => coordinates.map(() => 0));
	const pairs = [];
	coordinates.forEach((_, i) => coordinates.forEach((__, j) => i !== j && pairs.push([i, j])));
	const CONCURRENCY = 4;
	let succeeded = 0;
	let estimated = 0;
	for (let k = 0; k < pairs.length; k += CONCURRENCY) {
		await Promise.all(
			pairs.slice(k, k + CONCURRENCY).map(async ([i, j]) => {
				try {
					minutes[i][j] = await fetchPairMinutes(coordinates[i], coordinates[j], headers);
					succeeded++;
				} catch {
					minutes[i][j] = estimateMinutes(coordinates[i], coordinates[j]);
					estimated++;
				}
			})
		);
		if (!succeeded) throw new Error('OSRM unreachable');
	}
	if (estimated) log('warn', 'RouteService', `${estimated}/${pairs.length} travel time(s) estimated as the crow flies`);
	return { minutes, complete: !estimated };
}

const segmentLength = (indexes, texts) => indexes.reduce((length, i) => length + texts[i].length + 1, -1);

/**
 * Splits the matrix into OSRM `table` calls that each fit the proxy URL limit. Only pairs inside one group are
 * needed (a group = the points of one day, days are optimized independently): groups share a call while their
 * union fits, and a group too large for one call is cut into half-limit blocks, one call per pair of blocks.
 *
 * @param {string[]} texts  'lng,lat' of each point
 * @param {number[][]} groups  point indexes whose pairs are all needed
 * @returns {number[][]} point indexes of each call
 */
export function planTableCalls(texts, groups, maxLength = MAX_COORDINATES_LENGTH) {
	const fits = (indexes, limit = maxLength) => segmentLength(indexes, texts) <= limit;
	const calls = [];
	groups.forEach((group) => {
		const points = [...new Set(group)];
		if (points.length < 2) return;
		if (!fits(points)) {
			const blocks = [];
			points.forEach((i) => {
				const last = blocks[blocks.length - 1];
				if (last && fits([...last, i], Math.floor((maxLength - 1) / 2))) last.push(i);
				else blocks.push([i]);
			});
			blocks.forEach((a, k) => blocks.slice(k + 1).forEach((b) => calls.push([...a, ...b])));
			return;
		}
		const shared = calls.find((call) => fits([...new Set([...call, ...points])]));
		if (shared) points.forEach((i) => !shared.includes(i) && shared.push(i));
		else calls.push(points);
	});
	return calls;
}

// OSRM durations do not depend on the time of day: "Ré-optimiser" and reopening the same period reuse them.
const TABLE_CACHE_SIZE = 50;
const tableCache = new Map();

async function getTableMinutes(points, headers) {
	const segment = points.map(toCoordinateText).join(';');
	if (tableCache.has(segment)) return tableCache.get(segment);
	let minutes;
	try {
		const response = await axios.get(getMapsApiUrl(`/osrm/table/v1/driving/${segment}`), { params: { annotations: 'duration' }, headers, timeout: MATRIX_TIMEOUT_MS });
		const durations = response?.data?.durations;
		if (response?.data?.code !== 'Ok' || !Array.isArray(durations)) throw new Error('OSRM table format invalid');
		minutes = durations.map((row, i) => row.map((seconds, j) => (i === j ? 0 : Number.isFinite(seconds) ? Math.ceil(seconds / 60) : estimateMinutes(points[i], points[j]))));
	} catch (error) {
		log('warn', 'RouteService', `OSRM table failed (${error?.message || error}), one route call per pair instead`);
		const pairwise = await getPairwiseDurations(points, headers);
		if (!pairwise.complete) return pairwise.minutes;
		minutes = pairwise.minutes;
	}
	if (tableCache.size >= TABLE_CACHE_SIZE) tableCache.delete(tableCache.keys().next().value);
	tableCache.set(segment, minutes);
	return minutes;
}

/**
 * Driving time matrix in minutes (rounded up) between [lng, lat] points, from the same eCOS OSRM proxy as
 * the route drawing: a few OSRM `table` calls (see planTableCalls), per-pair `route` calls for a table call
 * that fails. Rejects when OSRM is unreachable. Cells outside the groups stay null. Never Google (each call
 * would count against the DirectionsService quota).
 *
 * @param {number[][]} coordinates  [lng, lat] of each point
 * @param {number[][]} [groups]  point indexes whose pairs are needed, all pairs by default
 */
async function getDurationMatrix(coordinates, groups = null) {
	if (!Array.isArray(coordinates) || coordinates.length < 2) return coordinates?.map(() => [0]) ?? [];
	const minutes = coordinates.map((_, i) => coordinates.map((__, j) => (i === j ? 0 : null)));
	const calls = planTableCalls(coordinates.map(toCoordinateText), groups ?? [coordinates.map((_, i) => i)]);
	if (!calls.length) return minutes;
	const headers = await getAuthHeaders({}, 'token');
	await Promise.all(
		calls.map(async (indexes) => {
			const points = indexes.map((i) => coordinates[i]);
			const block = await getTableMinutes(points, headers);
			indexes.forEach((i, a) => indexes.forEach((j, b) => (minutes[i][j] = block[a][b])));
		})
	);
	return minutes;
}

export const routeService = {
	getRoute: getRoute,
	getRouteWithFallback: getRouteWithFallback,
	getDurationMatrix: getDurationMatrix,
	setTravelMode: setTravelMode,
	getTravelMode: getTravelMode,
	isGoogleAllowed: isGoogleAllowed,
};
